<!--
	@component

	One of the homepage banners: a full-bleed photo with handwritten lettering, a short uppercase
	line under it and the buttons at the bottom.

	All of the banners use this component, so the lettering keeps one visual size and one position
	in every one of them – pass the asset and the copy, the layout is not meant to be overridden.
-->
<script lang="ts" module>
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Media from "@components/common/Media.svelte";
	import ImageSection from "@components/sections/ImageSection.svelte";
	import type { Snippet } from "svelte";
	import type { ClassValue } from "svelte/elements";
	import type { SourceSetQuery } from "@core/schema/SourceSetQuery.js";
	import { tw } from "@core/utils/svelte/tw.js";

	/**
	 * Viewport width at which the lettering reaches its full size; below it the lettering scales down
	 * with the viewport. Shared by every banner, so assets of different proportions still read as one
	 * set – what differs between them is only their own full size.
	 */
	const letteringFullSizeViewport = 508;

	const innerClass = tw(
		// Content fills the space above the buttons and is centered in it; the buttons stay at the bottom.
		// `min-h-0` lets that row take the height it is given instead of growing with its content,
		// which would push the buttons out of the banner.
		"!grid-rows-[1fr_auto] !pt-0 [&>*]:min-h-0",
		// The banner is only half of the viewport, so the container inside it has to measure itself
		// against the banner instead of the whole screen, otherwise its content ends up off-center.
		"lg:[--container-width:100%]",
		// Half the bottom inset of a full-width section.
		"![--pb:1.25rem] sm:![--pb:1.5rem] md:![--pb:1.75rem] lg:![--pb:2rem] xl:![--pb:2.25rem] 2xl:![--pb:2.5rem]",
	);

	// Wide enough for the line breaks written into the copy to survive instead of being re-wrapped.
	const taglineClass = tw(
		"uppercase text-center text-white font-bold max-w-140",
		"text-base xl:text-lg/normal tracking-[.17em]",
	);
</script>

<script lang="ts">
	const {
		actions,
		class: customClass,
		darkOverlay = false,
		lcp = false,
		lettering,
		media,
		sources,
		tagline,
		title,
		topAligned = false,
	}: {
		actions: Snippet;
		class?: ClassValue;
		darkOverlay?: boolean;
		lcp?: boolean;
		/** The handwritten asset. `width`/`height` are the size it reaches on wide screens. */
		lettering: { src: string; width: number; height: number; class?: ClassValue };
		/** Positioning of the background photo, e.g. `object-[50%_8.75%]`. */
		media?: ClassValue;
		sources: Partial<Record<SourceSetQuery, string>> & { default: string };
		/** Short uppercase line(s) below the lettering. */
		tagline: string;
		/** Wording of the lettering – it is the heading of the banner. */
		title: string;
		/**
		 * Anchors the lettering 60px below the top of the banner and the tagline 32px above the
		 * buttons, instead of centering the lettering/tagline group in the available space.
		 */
		topAligned?: boolean;
	} = $props();

	const letteringWidth = $derived(
		`min(${((lettering.width / letteringFullSizeViewport) * 100).toFixed(2)}vw, ${lettering.width}px)`,
	);
</script>

<ImageSection
	{actions}
	{darkOverlay}
	{lcp}
	{media}
	{sources}
	{innerClass}
	class={["w-full !h-[75vh] lg:!col-span-1", customClass]}
>
	{#snippet heading()}
		{#if topAligned}
			<div class="flex h-full flex-col items-center">
				<Heading level={2} text={title} class="pt-15">
					<div style:width={letteringWidth}>
						<Media
							width={lettering.width}
							height={lettering.height}
							sourcesets={{}}
							source={{ type: "image/webp", src: lettering.src }}
							alt=""
							wrapperClass="block"
							class={["w-full", lettering.class]}
						/>
					</div>
				</Heading>
				<p class={[taglineClass, "mt-auto mb-8"]}>
					<Formatted text={tagline} />
				</p>
			</div>
		{:else}
			<!--
				The two "1fr" rows are what keeps the lettering in the middle of the banner: they share the
				free space evenly, so the lettering stays centered no matter how long the tagline is. Only
				once the tagline no longer fits into its half does it take space from the row above it.
			-->
			<div class="grid h-full grid-rows-[1fr_auto_1fr] justify-items-center">
				<Heading level={2} text={title} class="row-start-2">
					<div style:width={letteringWidth}>
						<Media
							width={lettering.width}
							height={lettering.height}
							sourcesets={{}}
							source={{ type: "image/webp", src: lettering.src }}
							alt=""
							wrapperClass="block"
							class={["w-full", lettering.class]}
						/>
					</div>
				</Heading>
				<p class={[taglineClass, "row-start-3 mt-6 md:mt-8"]}>
					<Formatted text={tagline} />
				</p>
			</div>
		{/if}
	{/snippet}
</ImageSection>
